import { useDispatch, useSelector } from 'react-redux'
import { select } from "application/store";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AppRouters from "application/globals/app-routers";
import { useHistory } from "react-router-dom";
import { isEmptyString, isNullOrUndefined } from "presentation/common";
import eventBus from "infrastructure/common/eventBus";
import { LoadingEvents } from "presentation/common/components/style-guide/loading/LoadingEvents";
import { service } from "infrastructure/index";
import { IsNotOwner } from "presentation/new-report/NewReportConstant";

export const useNewReport = () => {
  // refs
  const generalDataRef = React.useRef();
  const dispatchDataRef = React.useRef();
  const planDataRef = React.useRef();
  const recaptchaRef = useRef();
  const billIssuerInnerRef = useRef();
  const comunaInnerRef = useRef();
  const regionInnerRef = useRef();
  const dispatchPlaceRef = useRef();
  const [disableFinishBtn, setDisableFinishBtn] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();

  const state = useSelector(
    select((models) => ({
      uiState: models.newRequest.ui,
      dataState: models.newRequest.data,
      userInfo: models.auth.user,
      isAuthenticated: models.auth.isAuthenticated
    }))
  );

  const uiState = state.uiState;
  const dataState = state.dataState;
  const isDirty =
    !uiState.disabledIsDirty &&
    (uiState.generalDataStepIsDirty
      || uiState.planStepIsDirty
      || uiState.dispatchStepIsDirty
      || uiState.paymentStepIsDirty
      || !isEmptyString(dataState.rut)
      || !isEmptyString(dataState.paymentType)
      || !isEmptyString(dataState.planSelected));

  const actions = React.useMemo(() => {
    return {
      resetAction: dispatch.newRequest.reset,
      updateGeneralDataIsDirtyAction: dispatch.newRequest.updateGeneralDataIsDirtyAction,
      setGeneralDataErrors: dispatch.newRequest.setGeneralDataErrors,
      updateGeneralDataAction: dispatch.newRequest.updateGeneralDataAction,
      updateDispatchDataAction: dispatch.newRequest.updateDispatchDataAction,
      onBackDispatchStepAction: dispatch.newRequest.onBackDispatchStepAction,
      updateBillInfoAction: dispatch.newRequest.updateBillInfoAction,
      deleteBillInfoAction: dispatch.newRequest.deleteBillInfoAction,
      updatePaymentTypeAction: dispatch.newRequest.updatePaymentTypeAction,
      paymentSelectedIsDirtyAction: dispatch.newRequest.paymentSelectedIsDirtyAction,
      planSelectedIsDirtyAction: dispatch.newRequest.planSelectedIsDirtyAction,
      dispatchSelectedIsDirtyAction: dispatch.newRequest.dispatchSelectedIsDirtyAction,
      resetReCaptchaTokenAction: dispatch.newRequest.resetReCaptchaTokenAction,
      setPlanDataAction: dispatch.newRequest.setPlanDataAction,
      setDispatchDataAction: dispatch.newRequest.setDispatchDataAction,
      setPlanDataErrorsAction: dispatch.newRequest.setPlanDataErrorsAction,
      setDispatchDataErrorsAction: dispatch.newRequest.setDispatchDataErrorsAction,
      onNewRequestAction: dispatch.newRequest.onNewRequestAction,
      onChangeAcceptTermAndConditionsAction: dispatch.newRequest.onChangeAcceptTermAndConditionsAction,
      loadBrandAndBillIssuerAction: dispatch.newRequest.loadBrandAndBillIssuerAction,
      loadDispatchBillIssuerAction: dispatch.newRequest.loadDispatchBillIssuerAction,
      getManagerByBillIssuerAction: dispatch.newRequest.getManagerByBillIssuerAction,

    }
  }, [dispatch]);


  useEffect(() => {
    async function fetchBillIssuer() {
      eventBus.dispatch(LoadingEvents.OPEN);
      await actions.loadBrandAndBillIssuerAction();
      eventBus.dispatch(LoadingEvents.CLOSE);
    }
    fetchBillIssuer();

  }, [actions]);


  const onDispatchPlaceSelect = useCallback(async (name) => {
    if (!name) {
      dispatch.newRequest.updateDispatchRegionDataAction([]);
      return resetDispatchForm();
    }

    const billIssuerLabel = dataState.billIssuerSelected?.label

    eventBus.dispatch(LoadingEvents.OPEN);

    const response = await actions.loadDispatchBillIssuerAction(
      name === "LOCAL DE COMPRA" && billIssuerLabel !== 'OTRO'
        ? `?name=${billIssuerLabel}`
        : ''
    );

    eventBus.dispatch(LoadingEvents.CLOSE);

    resetDispatchForm();

    if (response && response?.length === 1) {
      const [regionSelected] = response;
      dispatchDataRef?.current?.setFieldValue('region', regionSelected.region_uuid);
      dispatchDataRef?.current?.setFieldValue('regionSelected', {
        ...regionSelected,
        label: regionSelected.nombre,
        value: regionSelected.region_uuid
      });
      dispatchDataRef?.current?.setFieldTouched("region", false, false);
      dispatchDataRef?.current?.setFieldTouched("regionSelected", false, false);
    }
  }, [dataState.billIssuerSelected?.label])

  const onCancel = useCallback(() => {
    history.push(AppRouters.home);
  }, [history]);

  const validateGeneralData = useCallback(async () => {
    actions.updateGeneralDataIsDirtyAction(true);
    generalDataRef.current.handleSubmit();
    await generalDataRef.current.validateForm();

    actions.setGeneralDataErrors(generalDataRef.current.errors);
    regionInnerRef?.current?.clearValue();
    if (generalDataRef.current.isValid) {
      eventBus.dispatch(LoadingEvents.OPEN);
      actions.updateGeneralDataIsDirtyAction(false);
      const success = await actions.updateGeneralDataAction(generalDataRef.current.values);
      eventBus.dispatch(LoadingEvents.CLOSE);

      if (!success) {
        recaptchaRef?.current?.reset();
      }
      return success;
    }

    return false;
  }, [actions, generalDataRef]);

  const validatePaymentSelected = useCallback(() => {
    actions.paymentSelectedIsDirtyAction(true);
    return !isNullOrUndefined(state.dataState.paymentType);
  },
    [state.dataState, actions],
  );

  const validatePlanSelected = useCallback(async () => {
    actions.planSelectedIsDirtyAction(true);

    planDataRef.current.handleSubmit();
    await planDataRef.current.validateForm();

    actions.setPlanDataErrorsAction(planDataRef.current.errors);

    if (planDataRef.current.isValid) {
      actions.setPlanDataAction(planDataRef.current.values);
    }

    return planDataRef.current.isValid;
  },
    [actions, planDataRef],
  );

  const validateDispatchData = useCallback(async () => {
    actions.dispatchSelectedIsDirtyAction(true);
    dispatchDataRef.current.handleSubmit();

    await dispatchDataRef.current.validateForm();

    actions.setDispatchDataErrorsAction(dispatchDataRef.current.errors);
    setDispatchFormTouched(true);

    if (dispatchDataRef.current.isValid) {
      eventBus.dispatch(LoadingEvents.OPEN);
      const success = await actions.updateDispatchDataAction(dispatchDataRef.current.values);
      actions.dispatchSelectedIsDirtyAction(false);
      eventBus.dispatch(LoadingEvents.CLOSE);
      return success;
    }

    return false;
  }, [actions, dispatchDataRef])

  const onBackPlanStep = useCallback(() => {
    actions.planSelectedIsDirtyAction(false);
  },
    [actions]);

  const onBackDispatchStep = useCallback(() => {
    actions.onBackDispatchStepAction(dispatchDataRef.current.values);
    actions.dispatchSelectedIsDirtyAction(false);
    actions.resetReCaptchaTokenAction();
  },
    [actions]);

  const onChangeBillIssuer = useCallback(async (item) => {
    if (dispatchDataRef?.current?.values?.place === "LOCAL DE COMPRA") {
      actions.onBackDispatchStepAction({});
      generalDataRef.current?.setFieldValue('dispatchData', {})

      resetDispatchForm();
    }

    if (item) {
      eventBus.dispatch(LoadingEvents.OPEN);
      await actions.getManagerByBillIssuerAction(item.label);
      eventBus.dispatch(LoadingEvents.CLOSE);
    }

  }, [dataState.billIssuerSelected?.label])

  const setDispatchFormTouched = (value) => {
    dispatchDataRef?.current?.setFieldTouched("complement", value);
    dispatchDataRef?.current?.setFieldTouched("comuna", value);
    dispatchDataRef?.current?.setFieldTouched("number", value);
    dispatchDataRef?.current?.setFieldTouched("region", value);
    dispatchDataRef?.current?.setFieldTouched("street", value);
  }

  const resetDispatchForm = () => {
    dispatchDataRef?.current?.setFieldValue("complement", "");
    dispatchDataRef?.current?.setFieldValue("comuna", undefined);
    dispatchDataRef?.current?.setFieldValue("regionSelected", undefined);
    dispatchDataRef?.current?.setFieldValue("comunaSelected", undefined);
    dispatchDataRef?.current?.setFieldValue("number", "");
    dispatchDataRef?.current?.setFieldValue("region", undefined);
    dispatchDataRef?.current?.setFieldValue("street", "");

    comunaInnerRef?.current?.clearValue();
    regionInnerRef?.current?.clearValue();
    setDispatchFormTouched(false);
  }

  const onUpdatePlanData = useCallback((data) => {
    actions.setPlanDataAction(data);

  }, [actions]);

  const cleanBillIssuerSelected = useCallback(() => {
    if (generalDataRef.current.values?.billIssuerSelected?.unique_id === state.dataState?.billIssuerSelected?.unique_id) return;
    if (!state.dataState?.billIssuerSelected) {
      billIssuerInnerRef?.current?.clearValue();
    }
  }, []);

  const cleanOwnerInfo = useCallback(() => {
    const values = generalDataRef.current?.values;

    if (values.isOwner !== IsNotOwner) {
      generalDataRef.current?.resetForm({
        values: {
          ...values,
          rut: '',
          name: '',
          firstName: ''
        }
      });

      return;
    }

    if (values.personType === 'NATURAL') {
      generalDataRef.current?.resetForm({
        values: {
          ...values,
          personInfo: {
            ...values?.personInfo,
            rut: '',
            name: '',
            firstName: '',
          }
        }
      });

      return;
    }

    generalDataRef.current?.resetForm({
      values: {
        ...values,
        ownerBillInfo: {
          ...values?.ownerBillInfo,
          businessType: undefined,
          rut: '',
        }
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      actions.resetAction()
    };
  }, [actions]);

  useEffect(() => {
    generalDataRef?.current?.setFieldValue('files', state.dataState.files);
  }, [state.dataState.files]);

  useEffect(() => {
    service.eventBus.on('REFRESH_FORM:NOT_MATCH_RUT_EMISOR', cleanBillIssuerSelected);
    service.eventBus.on('REFRESH_FORM:INVALID_OWNER', cleanOwnerInfo);

    return () => {
      service.eventBus.remove(cleanBillIssuerSelected);
      service.eventBus.remove(cleanOwnerInfo);
    };
  }, []);

  return React.useMemo(() => ({
    ...state,
    isDirty,
    validateGeneralData,

    generalDataRef,
    dispatchDataRef,
    recaptchaRef,
    planDataRef,
    billIssuerInnerRef,
    comunaInnerRef,
    dispatchPlaceRef,
    regionInnerRef,
    onCancel,
    onDispatchPlaceSelect,
    validatePaymentSelected,
    validatePlanSelected,
    validateDispatchData,
    onBackPlanStep,
    updatePaymentType: actions.updatePaymentTypeAction,
    updateBillInfo: actions.updateBillInfoAction,
    deleteBillInfo: actions.deleteBillInfoAction,
    setPlanDataAction: actions.setPlanDataAction,
    setDispatchDataAction: actions.setDispatchDataAction,
    resetAction: actions.resetAction,
    onUpdatePlanData,
    onChangeBillIssuer,
    onNewRequest: () => {
      setDisableFinishBtn(true);
      actions.onNewRequestAction();
    },
    onChangeAcceptTermAndConditionsAction: actions.onChangeAcceptTermAndConditionsAction,
    disableFinishBtn,
    onBackDispatchStep
  }),
    [
      actions,
      validateGeneralData,
      validatePlanSelected,
      validateDispatchData,
      onBackPlanStep,
      generalDataRef,
      dispatchDataRef,
      recaptchaRef,
      planDataRef,
      billIssuerInnerRef,
      comunaInnerRef,
      dispatchPlaceRef,
      regionInnerRef,
      state,
      onCancel,
      onDispatchPlaceSelect,
      validatePaymentSelected,
      onUpdatePlanData,
      onChangeBillIssuer,
      isDirty,
      disableFinishBtn
    ],
  );
}
